import { useState } from "react";
import { useNavigate } from "react-router";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { Loader2, Trash2 } from "lucide-react";
import { toast } from "sonner";

import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Alert, AlertDescription } from "@/components/ui/alert";

import { api } from "@/lib/api";
import { useOnboarding } from "@/hooks/useOnboarding";
import { step7Schema } from "@/schemas/onboarding-schema";
import { AgeGroupInput } from "nautika-types";

import { OrgSelection } from "@/components/org-selection";
import FormShell from "@/components/form-shell";

export function OnboardingStep7() {
  const {
    organizations,
    showOrgSelection,
    selectedOrgId,
    setSelectedOrgId,
    isSettingActive,
    handleSetActiveOrganization,
    onCloseOrgSelection,
    updateOnboardingStep,
  } = useOnboarding("step7");

  const navigate = useNavigate();
  const [ageGroups, setAgeGroups] = useState<AgeGroupInput[]>([]);
  const [error, setError] = useState<string | null>(null);

  // Récupérer les groupes d'âge existants
  const { data: existingAgeGroups, isLoading: isLoadingAgeGroups } = api.useQuery("get", "/club/age-groups");

  // Formater les groupes existants; utiliser l'état local si l'utilisateur a commencé à modifier
  const formattedExisting: AgeGroupInput[] = (existingAgeGroups || []).map((group) => ({
    name: group.name,
    minAge: group.minAge,
    maxAge: group.maxAge,
  }));
  const effectiveAgeGroups = ageGroups.length > 0 ? ageGroups : formattedExisting;

  // Form pour l'ajout de groupes individuels
  const addGroupForm = useForm<AgeGroupInput>({
    resolver: zodResolver(step7Schema),
    defaultValues: {
      name: "",
      minAge: undefined,
      maxAge: undefined,
    },
  });

  // Form principal pour la soumission finale
  const mainForm = useForm({
    defaultValues: { ageGroups: effectiveAgeGroups },
  });

  // Mutation pour sauvegarder les groupes d'âge
  const { mutate: saveAgeGroups, isPending: isSaving } = api.useMutation("post", "/onboarding/groups", {
    onSuccess: async () => {
      toast.success("Groupes d'âge sauvegardés avec succès!");
      await updateOnboardingStep("step8");
      navigate("/onboarding/step8");
    },
    onError: (error) => {
      console.error("Error saving age groups:", error);
      toast.error("Erreur lors de la sauvegarde", {
        description: error.message || "Impossible de sauvegarder les groupes d'âge",
      });
    },
  });

  // Mutation pour générer les périodes (/club/init)
  const { mutate: generatePeriods, isPending: isGenerating } = api.useMutation("post", "/club/init", {
    onSuccess: () => {
      // toast.success("Périodes générées avec succès!");
    },
    onError: (error) => {
      console.error("Error generating periods:", error);
      toast.error("Erreur lors de la génération des périodes", {
        description: error.message || "Impossible de générer les périodes",
      });
    },
  });

  const handleSubmit = () => {
    // Vérifier qu'il y a au moins un groupe d'âge
    if (effectiveAgeGroups.length === 0) {
      toast.error("Groupe d'âge requis", {
        description: "Vous devez créer au moins un groupe d'âge avant de continuer",
      });
      return;
    }

    saveAgeGroups({
      body: { ageGroups: effectiveAgeGroups },
    });

    generatePeriods({}); // Empty body needed because it is a POST request
  };

  const validateNewGroup = (data: AgeGroupInput): string | null => {
    // Vérifier que l'âge minimum et maximum sont bien des nombres
    if (typeof data.minAge !== "number" || typeof data.maxAge !== "number") {
      return "L'âge minimum et maximum doivent être des nombres";
    }

    // Vérifier que tous les champs sont remplis
    if (!data.name || data.minAge === undefined || data.maxAge === undefined) {
      return "Remplir tous les champs avant d'ajouter un groupe";
    }

    // Vérifier la longueur du nom
    if (data.name.length > 30) {
      return "Nombre de caractères maximum : 30";
    }

    // Vérifier que l'âge minimum est inférieur à l'âge maximum
    if (data.minAge >= data.maxAge) {
      return "L'âge minimum doit être inférieur à l'âge maximum";
    }

    // Vérifier que le nom n'existe pas déjà
    if (effectiveAgeGroups.some((group) => group.name === data.name)) {
      return "Un groupe avec ce nom existe déjà";
    }

    // Vérifier le chevauchement des tranches d'âge
    if (
      effectiveAgeGroups.some(
        (group) =>
          (data.minAge >= group.minAge && data.minAge <= group.maxAge) ||
          (data.maxAge >= group.minAge && data.maxAge <= group.maxAge) ||
          (data.minAge <= group.minAge && data.maxAge >= group.maxAge)
      )
    ) {
      return "Cette tranche d'âge chevauche un groupe déjà créé";
    }

    return null;
  };

  const addGroup = async () => {
    const data = addGroupForm.getValues();
    const validationError = validateNewGroup(data);
    if (validationError) {
      setError(validationError);
      return;
    }

    setError(null);
    setAgeGroups([...effectiveAgeGroups, data]);
    addGroupForm.reset();
  };

  const deleteGroup = (index: number) => {
    setAgeGroups(effectiveAgeGroups.filter((_, i) => i !== index));
  };
  return (
    <>
      <FormShell title="Groupes d'âge" subtitle="Créez et personnalisez les groupes d'âge de votre club" onSubmit={mainForm.handleSubmit(handleSubmit)}>
        <div className="mx-auto max-w-2xl">
          {/* Afficher un loader pendant le chargement des groupes existants */}
          {isLoadingAgeGroups ? (
            <div className="flex items-center justify-center py-8">
              <Loader2 className="h-6 w-6 animate-spin" />
              <span className="ml-2 text-sm text-muted-foreground">Chargement des groupes existants...</span>
            </div>
          ) : (
            <>
              {/* Section d'ajout de groupe (sans form imbriqué) */}
              <Form {...addGroupForm}>
                <Card>
                  <CardContent className="flex flex-col gap-y-4 p-6">
                    <FormField
                      control={addGroupForm.control}
                      name="name"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>Nom du groupe</FormLabel>
                          <FormControl>
                            <Input placeholder="ex: Les petits" {...field} />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                    <div className="grid grid-cols-2 gap-4">
                      <FormField
                        control={addGroupForm.control}
                        name="minAge"
                        render={({ field }) => (
                          <FormItem>
                            <FormLabel>Âge min. (inclus)</FormLabel>
                            <FormControl>
                              <Input
                                type="number"
                                placeholder="3"
                                {...field}
                                value={field.value ?? ""}
                                onChange={(e) => {
                                  const v = e.target.value;
                                  const parsed = parseInt(v);
                                  field.onChange(v === "" || isNaN(parsed) ? undefined : parsed);
                                }}
                                min={0}
                              />
                            </FormControl>
                            <FormMessage />
                          </FormItem>
                        )}
                      />
                      <FormField
                        control={addGroupForm.control}
                        name="maxAge"
                        render={({ field }) => (
                          <FormItem>
                            <FormLabel>Âge max. (inclus)</FormLabel>
                            <FormControl>
                              <Input
                                type="number"
                                placeholder="6"
                                {...field}
                                value={field.value ?? ""}
                                onChange={(e) => {
                                  const v = e.target.value;
                                  const parsed = parseInt(v);
                                  field.onChange(v === "" || isNaN(parsed) ? undefined : parsed);
                                }}
                                min={0}
                              />
                            </FormControl>
                            <FormMessage />
                          </FormItem>
                        )}
                      />
                    </div>

                    {error && (
                      <Alert variant="destructive">
                        <AlertDescription>{error}</AlertDescription>
                      </Alert>
                    )}

                    <Button type="button" className="w-full" onClick={addGroup}>
                      Ajouter
                    </Button>
                  </CardContent>
                </Card>
              </Form>

              <div className="mt-12 flex flex-col">
                {effectiveAgeGroups.length === 0 ? (
                  <div className="rounded-md border border-dashed p-6 text-center">
                    <p className="text-sm text-muted-foreground">Aucun groupe d'âge créé. Utilisez le formulaire ci-dessus pour en créer un.</p>
                  </div>
                ) : (
                  <div className="flex flex-col gap-y-4">
                    {effectiveAgeGroups.map((group, index) => (
                      <Card className="py-4" key={index}>
                        <CardContent className="px-4">
                          <div className="flex items-center justify-between">
                            <div>
                              <div className="font-medium">{group.name}</div>
                              <div className="text-sm text-muted-foreground">
                                {group.minAge} à {group.maxAge} ans
                              </div>
                            </div>
                            <AlertDialog>
                              <AlertDialogTrigger asChild>
                                <Button variant="ghost" size="icon" className="text-destructive">
                                  <Trash2 className="h-5 w-5" />
                                  <span className="sr-only">Supprimer le groupe</span>
                                </Button>
                              </AlertDialogTrigger>
                              <AlertDialogContent>
                                <AlertDialogHeader>
                                  <AlertDialogTitle>Confirmer la suppression</AlertDialogTitle>
                                  <AlertDialogDescription>
                                    Êtes-vous sûr de vouloir supprimer ce groupe d'âge ? Cette action ne peut pas être annulée.
                                  </AlertDialogDescription>
                                </AlertDialogHeader>
                                <AlertDialogFooter>
                                  <AlertDialogCancel>Annuler</AlertDialogCancel>
                                  <AlertDialogAction onClick={() => deleteGroup(index)}>Supprimer</AlertDialogAction>
                                </AlertDialogFooter>
                              </AlertDialogContent>
                            </AlertDialog>
                          </div>
                        </CardContent>
                      </Card>
                    ))}
                  </div>
                )}
              </div>

              <div className="mt-8">
                <Button type="submit" className="w-full" disabled={isSaving || isGenerating}>
                  {isSaving || isGenerating ? (
                    <>
                      <Loader2 className="mr-2 h-5 w-5 animate-spin" />
                      Enregistrement...
                    </>
                  ) : (
                    "Continuer"
                  )}
                </Button>
              </div>
            </>
          )}
        </div>
      </FormShell>

      {/* Organisation selection dialog */}
      <OrgSelection
        open={showOrgSelection}
        organizations={organizations || []}
        selectedOrgId={selectedOrgId}
        onSelectedOrgChange={setSelectedOrgId}
        onConfirm={() => handleSetActiveOrganization(selectedOrgId)}
        onClose={onCloseOrgSelection}
        isLoading={isSettingActive}
      />
    </>
  );
}
